// ── FQL Structured Search · composables ──────────────────────────────────
// Vue 3 ports of the React hooks in fql-search-core.jsx. They own: the live
// query string + derived AST/filter/diagnostics/results, debounced async value
// suggestions, faceted value counts, and in-flight cancellation.
//
// useStructuredSearch returns a plain object whose getters read refs/computeds,
// so `search.query`, `search.results`, … stay reactive when read in templates
// or computeds. SearchBar, SearchSidebar, and ListEditShell all depend on this
// exact shape — preserve it.

import { ref, computed, unref, onUnmounted, watch } from 'vue'
import { FqlUtilities, FqlToMqlUtilities, SuggestionQueryUtilities } from '@frankai/fql'
import * as H from './fqlSearchHelpers.js'

const Fql = FqlUtilities

// ═══════════════════════════════════════════════════════════════════════
//  useStructuredSearch — the single source of truth
// ═══════════════════════════════════════════════════════════════════════
export function useStructuredSearch({ initialQuery = '', fieldDefinitions, data = [] }) {
  const query = ref(initialQuery)
  // fieldDefinitions / data may arrive as plain values, refs, or computeds.
  const fd = computed(() => unref(fieldDefinitions))
  const rows = computed(() => unref(data) || [])

  // React's setQuery accepted both a string and a functional updater; the AST
  // mutation helpers below rely on the updater form.
  const setQuery = (next) => {
    query.value = typeof next === 'function' ? next(query.value) : next
  }

  const ast = computed(() => {
    try {
      return Fql.parse(query.value)
    } catch {
      return { type: 'query', child: null, diagnostics: [], span: { start: 0, end: 0 } }
    }
  })

  const conv = computed(() => {
    try {
      return FqlToMqlUtilities.convert(query.value, fd.value, { posture: 'interactive' })
    } catch (e) {
      return {
        filter: null,
        diagnostics: [{ code: 'FQL-INTERNAL', severity: 'error', message: String((e && e.message) || e), span: { start: 0, end: query.value.length } }],
      }
    }
  })

  // Merge parse + convert diagnostics, de-duped by code+span.
  const diagnostics = computed(() => {
    const all = [...(ast.value.diagnostics || []), ...(conv.value.diagnostics || [])]
    const seen = new Set()
    const out = []
    for (const d of all) {
      const k = d.code + ':' + (d.span ? d.span.start + '-' + d.span.end : '?')
      if (seen.has(k)) continue
      seen.add(k)
      out.push(d)
    }
    return out
  })

  const results = computed(() => rows.value.filter((d) => H.matchDoc(d, conv.value.filter)))
  const fieldView = computed(() => H.buildFieldView(ast.value, fd.value))

  const api = {
    toggleValue: (field, value) => setQuery((q) => H.toggleValue(q, field, value, fd.value)),
    setRange: (field, range) => setQuery((q) => H.setRange(q, field, range, fd.value)),
    setValue: (field, value) => setQuery((q) => H.setValue(q, field, value, fd.value)),
    clearField: (field) => setQuery((q) => H.clearField(q, field, fd.value)),
    removeConjunct: (index) => setQuery((q) => H.removeConjunct(q, index)),
    replaceSpan: (start, end, text) => setQuery((q) => H.replaceSpan(q, start, end, text)),
  }

  return {
    // expose the raw ref too, for callers that want to watch it directly
    queryRef: query,
    get query() {
      return query.value
    },
    setQuery,
    get ast() {
      return ast.value
    },
    get filter() {
      return conv.value.filter
    },
    get diagnostics() {
      return diagnostics.value
    },
    get matchCount() {
      return results.value.length
    },
    get results() {
      return results.value
    },
    get fieldView() {
      return fieldView.value
    },
    ...api,
  }
}

// ═══════════════════════════════════════════════════════════════════════
//  useValueSuggestions — debounced, cancel-aware async value provider
// ═══════════════════════════════════════════════════════════════════════
// FQL builds the spec; we own debounce, stale-discard, error/empty state.
// status: idle → loading → (ready | empty | error). Static specs resolve
// synchronously (no debounce); query specs hit the injected valueProvider.
// All inputs are reactive getters/refs so the composable re-fires on change.
export function useValueSuggestions(opts) {
  const get = (k, dflt) => {
    const v = opts[k]
    return v === undefined ? dflt : unref(typeof v === 'function' && k !== 'valueProvider' ? v() : v)
  }
  const state = ref({ status: 'idle', values: [], error: null, source: null })
  let reqId = 0
  let timer = null

  const field = computed(() => get('field', null))
  const prefix = computed(() => get('prefix', ''))
  const enabled = computed(() => get('enabled', true))
  const fieldDefinitions = computed(() => get('fieldDefinitions', null))
  const limit = computed(() => get('limit', 8))
  const debounceMs = computed(() => get('debounceMs', 220))
  const valueProvider = () => opts.valueProvider

  onUnmounted(() => {
    reqId++
    clearTimeout(timer)
  })

  const fire = (force) => {
    let spec
    try {
      spec = SuggestionQueryUtilities.buildValueSuggestionQuery(field.value, prefix.value, fieldDefinitions.value, { limit: limit.value })
    } catch (e) {
      state.value = { status: 'error', values: [], error: String((e && e.message) || e), source: null }
      return
    }

    if (spec.type === 'static') {
      state.value = { status: spec.values.length ? 'ready' : 'empty', values: spec.values, error: null, source: 'static' }
      return
    }
    // query spec → backend. Bump the request id; stale responses (an earlier
    // prefix resolving after a newer one) are discarded on return.
    const myId = ++reqId
    state.value = { status: 'loading', values: force ? [] : state.value.values, error: null, source: 'query' }
    Promise.resolve(valueProvider()(spec))
      .then((vals) => {
        if (myId !== reqId) return // stale — discard
        const v = (vals || []).slice(0, limit.value)
        state.value = { status: v.length ? 'ready' : 'empty', values: v, error: null, source: 'query' }
      })
      .catch((err) => {
        if (myId !== reqId) return // stale — discard
        state.value = { status: 'error', values: [], error: String((err && err.message) || err || 'Request failed'), source: 'query' }
      })
  }

  watch(
    [field, prefix, enabled, debounceMs, fieldDefinitions, limit],
    () => {
      if (!enabled.value || !field.value) {
        reqId++
        state.value = { status: 'idle', values: [], error: null, source: null }
        return
      }
      // Peek the spec kind to decide debounce vs instant.
      let kind = 'query'
      try {
        kind = SuggestionQueryUtilities.buildValueSuggestionQuery(field.value, prefix.value, fieldDefinitions.value, { limit: limit.value }).type
      } catch {
        /* ignore */
      }
      if (kind === 'static') {
        fire(false)
        return
      }
      clearTimeout(timer)
      timer = setTimeout(() => fire(false), debounceMs.value)
    },
    { immediate: true },
  )

  const retry = () => fire(true)
  return {
    get status() {
      return state.value.status
    },
    get values() {
      return state.value.values
    },
    get error() {
      return state.value.error
    },
    get source() {
      return state.value.source
    },
    retry,
  }
}

// ═══════════════════════════════════════════════════════════════════════
//  useFacetValues — available values for a low-cardinality field, counted
//  against the CURRENT filter. Fetches when `enabled` (the section is
//  expanded) and re-fetches, debounced, whenever `query` changes. Same
//  cancel-aware, stale-discard lifecycle as useValueSuggestions; the shape is
//  [{ value, count }] — the provider decides faceting semantics.
// ═══════════════════════════════════════════════════════════════════════
export function useFacetValues(opts) {
  const get = (k, dflt) => {
    const v = opts[k]
    return v === undefined ? dflt : unref(typeof v === 'function' && k !== 'facetProvider' ? v() : v)
  }
  const state = ref({ status: 'idle', values: [], error: null })
  let reqId = 0
  let timer = null

  const field = computed(() => get('field', null))
  const query = computed(() => get('query', ''))
  const enabled = computed(() => get('enabled', true))
  const debounceMs = computed(() => get('debounceMs', 220))
  const facetProvider = () => opts.facetProvider

  onUnmounted(() => {
    reqId++
    clearTimeout(timer)
  })

  const fire = () => {
    if (typeof facetProvider() !== 'function') {
      state.value = { status: 'error', values: [], error: 'No facetProvider supplied' }
      return
    }
    const myId = ++reqId
    state.value = { status: 'loading', values: state.value.values, error: null }
    Promise.resolve(facetProvider()({ field: field.value, query: query.value }))
      .then((vals) => {
        if (myId !== reqId) return // stale — discard
        const v = (vals || []).map((o) => (o && typeof o === 'object' ? { value: String(o.value), count: o.count } : { value: String(o), count: null }))
        state.value = { status: v.length ? 'ready' : 'empty', values: v, error: null }
      })
      .catch((err) => {
        if (myId !== reqId) return // stale — discard
        state.value = { status: 'error', values: [], error: String((err && err.message) || err || 'Request failed') }
      })
  }

  watch(
    [field, query, enabled, debounceMs],
    () => {
      if (!enabled.value || !field.value) {
        reqId++
        clearTimeout(timer)
        state.value = { status: 'idle', values: [], error: null }
        return
      }
      clearTimeout(timer)
      timer = setTimeout(fire, debounceMs.value)
    },
    { immediate: true },
  )

  return {
    get status() {
      return state.value.status
    },
    get values() {
      return state.value.values
    },
    get error() {
      return state.value.error
    },
    retry: fire,
  }
}
